import { createSlice, createSelector } from '@reduxjs/toolkit'
import {
    getMapSettings,
    getLockedRooms,
    getAllRoomsData,
    roomsIdHaveEvent,
    getMeetingsByRoomId,
    getMaps,
    getMapsByTenant,
    getRoomsWithReception

} from './MapDisplayThunks';
import socketFactory, { mapSocketFactory } from '../../../utils/socketFactory';
import jsonStableStringify from 'json-stable-stringify';



const initialState = {
    mapConfig: {
        mapId: "",
        path: "",
        minZoom: 0,
        maxZoom: 0,
        backgroundColor: "",
        name: "",
        bounds: "",
        maxBounds: "",
        avatarBounds: [[0], [0]],
        defaultZoom: null,
        visibilityZoomThreshold: null
    },
    maps: [],
    loader: false,
    zoomed: false,
    MapsMinimized: [] ,
    isMapReady: false,


    receptionRoomsData: [],


    dialog: {
        title: "",
        text: ""
    },
    markerOpacity: 1,
    roomsData: [],
    zoomLevel: 1,
    reservedRooms: [],
    lockedRooms: [],
    selectedRoom: null,
    upcomingMeetings: [],
    futureMeetings: [],
    ongoingMeetings: [],
    anchorPosition: "",
    updateRoom: false,

    focusedAvatarId: null, // ID of the avatar that is currently focused
    currentRoom: null, // The room that is currently selected
}
const applyMapConfig = (state, payload) => {
    state.mapConfig.mapId = payload.id;
    state.mapConfig.path = payload.path;
    state.mapConfig.minZoom = payload.min_zoom;
    state.mapConfig.maxZoom = payload.max_zoom;
    state.mapConfig.backgroundColor = payload.background_color;
    state.mapConfig.name = payload.name;
    state.mapConfig.bounds = [
        [payload.south_west_lat, payload.south_west_lng],
        [payload.north_east_lat, payload.north_east_lng],
    ];
    state.mapConfig.maxBounds = [
        [payload.max_south_west_lat, payload.max_south_west_lng],
        [payload.max_north_east_lat, payload.max_north_east_lng],
    ];
    state.mapConfig.avatarBounds = [
        [payload.avatar_south_west_lat, payload.avatar_south_west_lng],
        [payload.avatar_north_east_lat, payload.avatar_north_east_lng],
    ];
    state.mapConfig.defaultZoom = payload.default_zoom;
    state.mapConfig.visibilityZoomThreshold = payload.visibility_zoom_threshold;
};
const setRoomsData = (state, data) => {
    const roomsData = data
        .filter(item => {
            const hasValidCoordinates = item && item.room && item.room.lat !== null && item.room.lng !== null;
            if (!hasValidCoordinates) {
                console.warn("Salle avec coordonnées invalides :", item);
            }
            return hasValidCoordinates;
        })
        .map(item => ({
            room_id: item.room_id || item.id,
            label: item.label,
            map_id: item.room?.map_id,
            chair_count: parseInt(item?.chair_count) || 0, // ✅ added chair count
            position: {
                lat: item.room.lat,
                lng: item.room.lng
            },
            is_reception: item?.is_reception, // 
            roomPermissions: item.roomPermissions ,
            objects : item.room.object_maps,
            map_clickable_objects: item.room.map_clickable_objects
        }));

    if (roomsData.length === 0) {
        console.warn("Aucune salle valide trouvée.");
    }

    // Compare previous and new state to avoid unnecessary rerenders
    const statesAreEqual = jsonStableStringify(state.roomsData) === jsonStableStringify(roomsData);
    if (statesAreEqual) {
        console.log("Rooms data is identical, no state update needed.");
        return;
    }

    state.roomsData = roomsData;
};


const fetchReservedRooms = (state, reservations) => {
    if (!Array.isArray(reservations)) {
        state.reservedRooms = [];
        return;
    }
    state.reservedRooms = reservations.map(Number);
}
const fetchLockedRooms = (state, lockedIdRooms) => {
    const newRoomIds = lockedIdRooms.map(room => room.id);
    state.lockedRooms.push(...newRoomIds);
}

const setMapViewData = (state, responseData) => {
    state.upcomingMeetings = responseData;
}

const MapDisplaySlice = createSlice({
    name: "mapDisplay",
    initialState,
    reducers: {
        setMapConfig: (state, action) => {
            applyMapConfig(state, action.payload);
        },
        setLoader: (state, action) => {
            state.loader = action.payload;
        },
        setDialog: (state, action) => {
            state.dialog.text = action.payload.text;
            state.dialog.title = action.payload.title;
        },
        setZoomed: (state, action) => {
            state.zoomed = action.payload;
        },
        setMarkerOpacity: (state, action) => {
            state.markerOpacity = action.payload;
        },
        setZoomLevel: (state, action) => {
            state.zoomLevel = action.payload;
        },
        setReservedRooms: (state, action) => {
            state.reservedRooms = action.payload;
        },
        setLockedRooms: (state, action) => {
            state.lockedRooms = action.payload;
        },
        setRoomId: (state, action) => {
            state.roomId = action.payload;
        },
        setAnchorPosition: (state, action) => {
            state.anchorPosition = action.payload;
        },
        setFocusedAvatarId: (state, action) => {
            state.focusedAvatarId = action.payload;
        },
        setCurrentRoom: (state, action) => {
            if (state.currentRoom?.room_id === action.payload?.room_id) {
                return;
            }
            state.currentRoom = action.payload;
        },
        setSelectedRoom: (state, action) => {
            state.selectedRoom = action.payload;
        },
        setUpcomingMeetings: (state, action) => {
            state.upcomingMeetings = action.payload;
        },
        setFutureMeetings: (state, action) => {
        state.futureMeetings = action.payload;
        },
        setOngoingMeetings: (state, action) => {
            state.ongoingMeetings = action.payload;
        },
        setIsMapReady: (state, action) => {
            state.isMapReady = action.payload;
        },
        ROOMS_LOCKED: (state, action) => {
            const payload = action.payload;

            if (!payload || !payload.data) {
                console.warn("ROOMS_LOCKED: payload.data is missing or null", payload);
                return;
            }

            const { is_locked, room_id } = payload.data;

            if (is_locked && !state.lockedRooms.includes(room_id)) {
                state.lockedRooms.push(room_id);
            } else if (!is_locked && state.lockedRooms.includes(room_id)) {
                state.lockedRooms = state.lockedRooms.filter(id => id !== room_id);
            }
        },
        NEW_RES: (state, action) => {
            const { room_id, isReserved } = action.payload.data;

            const roomIdNumber = Number(room_id);
            const exists = state.reservedRooms.includes(roomIdNumber);

            if (isReserved && !exists) {
                state.reservedRooms.push(roomIdNumber);

            } else if (!isReserved && exists) {
                state.reservedRooms = state.reservedRooms.filter(
                    id => id !== roomIdNumber
                );
            }

        },
        UP_RES: (state, action) => {
            if (!action.payload.data.data) {
                state.updateRoom = !state.updateRoom;
            }
        },
        RES_EXPIRED: (state, action) => {
        const room_id = Number(action.payload?.data?.room_id);
        if (!room_id) return;
        const hasUpcomingForRoom = Array.isArray(state.upcomingMeetings)
        ? state.upcomingMeetings.length > 0
       : false;
        if (hasUpcomingForRoom) {
        return;
        }
        state.reservedRooms = state.reservedRooms.filter(id => id !== room_id);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMapSettings.fulfilled, (state, action) => {
                applyMapConfig(state, action.payload);
            })
            .addCase(getAllRoomsData.fulfilled, (state, action) => {
                setRoomsData(state, action.payload);
            })
            .addCase(roomsIdHaveEvent.fulfilled, (state, action) => {
                fetchReservedRooms(state, action.payload);
            })
            .addCase(getLockedRooms.fulfilled, (state, action) => {
                fetchLockedRooms(state, action.payload);
            })
            .addCase(getMeetingsByRoomId.fulfilled, (state, action) => {
                setMapViewData(state, action.payload);
            })
            .addCase(getMaps.fulfilled, (state, action) => {
                state.maps = action.payload;
            })
           .addCase(getMapsByTenant.fulfilled, (state, action) => {
                state.MapsMinimized = action.payload?.maps;
            })
            .addCase(getRoomsWithReception.fulfilled, (state, action) => {
                console.log("REDUX RECEIVE DATA:", action.payload); // Ce log s'affiche-t-il ?
                state.receptionRoomsData = action.payload;})
    }
})

export const {
    setMapConfig,
    setLoader,
    setDialog,
    setZoomed,
    setSelectedRoom,
    setMarkerOpacity,
    setZoomLevel,
    setRoomId,
    setAnchorPosition,
    setIsMapReady,
    setReservedRooms,
    setFocusedAvatarId,
    setCurrentRoom,
    setFutureMeetings,
    setOngoingMeetings, 
    ROOMS_LOCKED,
    NEW_RES,
    UP_RES,
    RES_EXPIRED    
} = MapDisplaySlice.actions

socketFactory.on(`newReservationInfo`, NEW_RES);

socketFactory.on(`updateReservationInfo`, UP_RES);

mapSocketFactory.on('roomsLocked', ROOMS_LOCKED);

mapSocketFactory.on('onRoomUpdated', getAllRoomsData);

socketFactory.on('reservationExpired', RES_EXPIRED); 

export const joinRoomSocket = mapSocketFactory.emit("joinRoom", "interactModal/joinRoom");
export const leaveRoomSocket = mapSocketFactory.emit("leaveRoom", "interactModal/leaveRoom");

export const selectRoomsObject = createSelector(
    (state) => state.MapDisplay.roomsData,
    (rooms) => {
        return rooms.reduce((acc, room) => {
            acc[room.room_id] = room;
            return acc;
        }, {});
    }
)


export default MapDisplaySlice.reducer;